import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, switchMap, take, tap, filter } from 'rxjs/operators';

import { CookieService } from 'ngx-cookie-service';
import { ExternalNavigateService } from '@nrwl/rx-service-external-navigate';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { LoginModel, SnackbarType } from '@nrwl/rx-models';
import { UserModel } from '@nrwl/rx-models';
import { environment } from '@nrwl/environment';
import { DialogService } from '@nrwl/dialog-service';
import { RxPopoverAuthComponent } from 'libs/rx-ui/src/lib/rx-popover-auth/rx-popover-auth.component';
import { ErrorService } from '../error/error.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { DOCUMENT } from '@angular/common';
import { RouterService } from '../router/router.service';
import { RxDialogComponent } from '@nrwl/rx-dialog-template';
import { ClientsListDialogConfigService } from '../dialogs/clients/clients-list-dialog-config.service';
import { AUTH_SESSION_CLEANUP, AuthSessionCleanup } from './auth-session-cleanup.token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  env: any = environment;
  public userChanged: Subject<UserModel> = new Subject<UserModel>();
  private _user: UserModel = new UserModel();
  private snackbarType = SnackbarType;
  private envisionType: string;
  constructor(
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private cookieService: CookieService,
    private externalNavigate: ExternalNavigateService,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private snackbarService: SnackbarService,
    @Inject(DOCUMENT) private document: Document,
    private routerService: RouterService,
    private _dialogConfig: ClientsListDialogConfigService,
    @Optional()
    @Inject(AUTH_SESSION_CLEANUP)
    private readonly sessionCleanupHandlers: readonly AuthSessionCleanup[] | null,
    private readonly httpBackend: HttpBackend,
  ) {}

  get user() {
    return this._user;
  }

  set user(value: UserModel) {
    this._user = value;
    this.userChanged.next(this._user);
  }

  public isLoggedIn(): boolean {
    const result = !!localStorage.getItem(environment.localStorage.token);
    return result;
  }

  public getToken(): string {
    const token = localStorage.getItem(environment.localStorage.token) || '';
    return token.replace(/"/g, '');
  }

  public setToken(token) {
    if (this.getToken() === token) {
      return;
    }
    localStorage.setItem(environment.localStorage.token, token);
    console.log('Token set:', token);
  }

  public setCompanyId(id) {
    localStorage.setItem(environment.localStorage.companyId, id);
  }

  public setDepId(partner: string, id: string): void {
    localStorage.setItem(`depId_${partner}`, id);
  }

  public getDepId(partner: string): string {
    return localStorage.getItem(`depId_${partner}`) || '';
  }

  public logDebug(token, status = '200') {
    const data = (localStorage.getItem(environment.localStorage.debug) || []) as Array<any>;
    data.push({ token: token, date: new Date(), status: status });
    if (data.length > 10) {
      data.splice(0, 1);
    }
    localStorage.setItem(environment.localStorage.debug, <any>data);
  }

  public setWrap(flag: boolean) {
    localStorage.setItem(environment.localStorage.wrap, <any>flag);
  }

  public getWrap(): any {
    return localStorage.getItem(environment.localStorage.wrap) || false;
  }

  public getSchedulerToken(): string {
    return localStorage.getItem(environment.localStorage.schedulerToken) || '';
  }

  public setSchedulerToken(token) {
    localStorage.setItem(environment.localStorage.schedulerToken, token);
  }

  public getUserInfo(): any {
    return localStorage.getItem(environment.localStorage.userInfo) || '';
  }

  public userChangedEmit() {
    this.userChanged.next(this._user);
  }

  public setUserId(userId): void {
    localStorage.setItem(environment.localStorage.userInfo, userId);
    this.cookieService.set('user_id', userId, undefined, '/');
  }

  public getUserId(): any {
    return localStorage.getItem(this.env.localStorage.userInfo) || '';
  }

  public selectFacility(value, specificUser): Observable<any> {
    const url = environment.baseAPIUrlv3 + 'authenticate/doctor/';
    const payload = {
      buttons: value.facilities,
      title: 'Select Facility',
    };
    return this.dialogService.prompt(RxPopoverAuthComponent, { payload }).pipe(
      switchMap((result: any) => {
        this.cookieService.set('from_streamline', 'true', undefined, '/');
        this.cookieService.set('facility', result.id, undefined, '/');
        const user = {
          password: specificUser.Password,
          username: value.username,
          'x-skip-interceptor': true,
        };
        return this.http.post(url, user);
      }),
    );
  }

  public login(user: LoginModel, remember = false) {
    // this.store.dispatch(new SpinnerStatus(true));
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const timezone =
      window.location.origin === this.env.rxAdmin
        ? {}
        : {
            user_timezone_name: zone,
          };
    let specificUser: any = {
      ...user,
      ...timezone,
      'x-skip-interceptor': true,
    };
    const { username, password, depId } = user;
    const domain = this.document.domain;
    // Test staging authentication

    let url = environment.baseAPIUrlv3 + 'authenticate/doctor/';

    if (this.router.url.includes('mindbody')) {
      url = environment.integrationBaseAPIUrlV3 + 'deferred-authenticate/mindbody/';
      const user_timezone_name = Intl.DateTimeFormat().resolvedOptions().timeZone;
      specificUser = {
        username,
        password,
        StudioID: depId,
        user_timezone_name,
        'x-skip-interceptor': true,
      };
      return this.http.post(url, specificUser).pipe(
        switchMap((user: any) => {
          url = environment.baseAPIUrlv3 + 'authenticate/doctor/';
          specificUser = { ...user, 'x-skip-interceptor': true };
          return this.http.post(url, specificUser);
        }),
      );
    }

    if (this.router.url.includes('spc')) {
      url = environment.integrationBaseAPIUrlV3 + 'deferred-authenticate/spc/';
      const user_timezone_name = Intl.DateTimeFormat().resolvedOptions().timeZone;
      specificUser = {
        username,
        password,
        ClinicID: depId,
        user_timezone_name,
        'x-skip-interceptor': true,
      };
    }

    if (this.router.url.includes('streamline')) {
      url = environment.integrationBaseAPIUrlV3 + 'deferred-authenticate/streamline/';
      const user_timezone_name = Intl.DateTimeFormat().resolvedOptions().timeZone;
      specificUser = {
        UserName: username,
        Password: password,
        PracticeID: depId,
        user_timezone_name,
        'x-skip-interceptor': true,
      };
      return this.http.post(url, specificUser).pipe(
        filter((data) => !!data),
        switchMap((res: any) => this.selectFacility(res, specificUser)),
      );
    }

    if (this.router.url.includes('patientnow-pro')) {
      const user_timezone_name = Intl.DateTimeFormat().resolvedOptions().timeZone;
      url = environment.integrationBaseAPIUrlV3 + 'deferred-authenticate/patientnow/';
      specificUser = {
        username,
        password,
        PracticeID: user.depId,
        user_timezone_name,
        'x-skip-interceptor': true,
      };
    }
    if (
      this.router.url.includes('envisionnow') ||
      this.router.url.includes('patientnow-essentials')
    ) {
      const user_timezone_name = Intl.DateTimeFormat().resolvedOptions().timeZone;
      url = environment.integrationBaseAPIUrlV3 + 'deferred-authenticate/ennoview/';
      this.envisionType = this.router.url.includes('envisionnow')
        ? 'envisionnow'
        : 'patientnow-essentials';
      specificUser = {
        username,
        password,
        CompanyId: user.depId,
        user_timezone_name,
        'x-skip-interceptor': true,
      };
    }

    if (window.location.origin === this.env.rxAdmin) {
      url = environment.baseAPIUrlv3 + 'authenticate/rxstaff/';
      specificUser = {
        username,
        password,
        remember: false,
        isAdmin: true,
      };
    }

    localStorage.setItem('rx.path', user.username);
    return this.http.post(url, specificUser);
  }

  public ssoLogin(email: string) {
    return this.http.get(`${environment.ssoBaseUrl}`, {
      params: { email },
    });
  }

  setInfo() {}

  private setAuth(payload, remember = false) {
    localStorage.setItem(environment.localStorage.userInfo, payload.user);
    localStorage.setItem(environment.localStorage.token, payload.token);
    // this.setCookie(remember);
    return { success: true };
  }

  public afterLogout() {
    this.clearStorage();
    this._user = new UserModel();

    setTimeout(() => {
      this.redirectByDomain();
    });
  }

  public logout(isUnauthorizedError = false) {
    if (isUnauthorizedError) {
      //this.logDebug(this.getToken(), '401');
    }
    try {
      (window as any).webkit.messageHandlers.rxEventLogout.postMessage({
        flag: true,
      });
    } catch (e) {}

    this.authenticateLogout()
      .pipe(
        catchError((error) => {
          console.log('WARNING: Logout error', error);
          return of(null);
        }),
        take(1),
      )
      .subscribe((res: any) => {
        // result: {tokens_deleted: 1}
        localStorage.setItem(environment.localStorage.lastUserActivity, Date.now().toString());
        this.afterLogout();
      });
  }

  public clearStorage(clearAll = false) {
    this.runSessionCleanup();

    //const debugData = localStorage.getItem(this.env.localStorage.debug) || [];
    const dep = Object.keys(localStorage).filter((key) => key.startsWith('depId'));
    const depMap = new Map<string, string>();
    dep.forEach((key) => depMap.set(key, <any>localStorage.getItem(key)));

    localStorage.clear();
    localStorage.clear();

    if (!clearAll) {
      dep.forEach((key) => localStorage.setItem(key, <any>depMap.get(key)));
    }

    // Altair cache
    this.cookieService.delete('user_id', '/');
    this.cookieService.delete('token', '/');
    this.cookieService.delete('emrRestriction', '/');
    this.cookieService.delete('facility', '/');
    this.cookieService.delete('facilities', '/');
    this.cookieService.delete('impersonate', '/');
    this.cookieService.delete('from_streamline', '/');
    this.cookieService.delete('sso_impersonate', '/');
    //localStorage.setItem(this.env.localStorage.debug, debugData);
  }

  private runSessionCleanup(): void {
    this.sessionCleanupHandlers?.forEach((cleanup: AuthSessionCleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.warn('Session cleanup failed', error);
      }
    });
  }

  /**
   * Logout user but with saving current URL
   * Using this method for logout inactive users
   */
  public inactiveUserLogout() {
    if (this.externalNavigate.isExternalPath) {
      this.logout();
      return;
    }

    // save only return url in local storage
    const returnUrl = localStorage.getItem(this.env.localStorage.returnUrl);
    const localBuildVersion = localStorage.getItem(this.env.localStorage.version);
    this.clearStorage();
    // localStorage.setItem(this.env.localStorage.returnUrl, returnUrl);
    localStorage.setItem(this.env.localStorage.version, <any>localBuildVersion);

    this._user = new UserModel();

    this.redirectByDomain();
  }

  public resetPassword(params: { password: string; confirmPassword: string }): void {
    this.clearStorage();
    const { password: new_password1, confirmPassword: new_password2 } = params;
    const url = environment.baseAPIUrlv3 + 'authenticate/reset-password/';
    const currentLink = this.router.url.split('/');
    const token = currentLink[currentLink.length - 1];
    const uidb64 = currentLink[currentLink.length - 2];

    this.http
      .post(url, {
        new_password1,
        new_password2,
        token,
        uidb64,
        'x-skip-interceptor': true,
      })
      .pipe(
        take(1),
        tap(() => {
          this.router.navigate(['login']);
          this.snackbarService.success('Password was reset successfully');
        }),
        catchError((err: any) => {
          console.log('resetPassword error: ', err);
          let text = (<any>Object.values(err.error)[0]).toString().replace(',', '\n');
          if (text === 'true') {
            text = 'An unexpected error occurred.';
          }
          this.errorService.notify(text);
          return throwError(err);
        }),
      )
      .subscribe();
  }

  public forgotPassword(email: string): void {
    this.clearStorage();
    const url = environment.baseAPIUrlv3 + 'authenticate/forgot-password/';
    this.http
      .post(url, {
        email,
        domain_override: document.location.host,
        'x-skip-interceptor': true,
      })
      .pipe(
        take(1),
        tap(() => {
          this.router.navigate(['/login']);
          //this.router.navigate(['/v19/login']);
          this.snackbarService.success(
            'Reset password link was sent to your email. If you do not receive our message within 30 minutes, please check your Spam folder',
          );
        }),
        catchError((err: any) => {
          console.log('err', err);
          const text = (<any>Object.values(err.error)[0]).toString().replace(',', '\n');
          this.errorService.notify(text);
          return err;
        }),
      )
      .subscribe();
  }

  authenticateLogout(delete_all_tokens = false) {
    return this.http.post(`${environment.baseAPIUrlv3}authenticate/logout/`, {
      delete_all_tokens: delete_all_tokens,
    });
  }

  checkedUrl() {
    localStorage.removeItem(environment.localStorage.token);
    localStorage.removeItem('rx.path');
    this.router.navigate(['/login'], { queryParams: { token: null } });
    //this.router.navigate(['/v19/login'], { queryParams: { token: null } });
  }

  redirect(val: any): void {
    const clinicTitle = this.routerService.data.title;
    const currentEMRLink = this.router.url;
    const currentLinkLength = currentEMRLink.split('/').length;
    const canvaOAuth = localStorage.getItem(this.env.localStorage.canvaOAuth);

    localStorage.setItem('user', val.payload.user.id);
    localStorage.setItem('user_id', val.payload.user.id);
    this.setToken(val.payload.token);

    const urlParams = new URLSearchParams(window.location.search);
    if (this.hasCanvaOAuthData(urlParams)) {
      this.setCanvaOAuthData(urlParams);
      this.processCanvaOAuth();
      return;
    }

    if (!val.payload.user.user_type) {
      document.location.href = `${this.env.rxAdmin}?token=${val.payload.token}&id=${val.payload.user.id}`;
      return;
    }
    if (val.payload) {
      this.http
        .get(`${environment.baseAPIUrlv3}doctor/${val.payload.user.id}/`)
        .pipe(take(1))
        .subscribe((doctor: any): any => {
          if (!val.payload) {
            return;
          }

          if (this.handleCanvaIntegration(doctor.clinic.id)) {
            return;
          }

          const { token, user } = val.payload;
          const clinicEMR = doctor.clinic.emr_integration;
          const fromStreamLine = this.cookieService.get('from_streamline');
          const facility = this.cookieService.get('facility');

          let urlParams = `?token=${token}&id=${user.id}`;

          if (clinicEMR) {
            urlParams += `&emr=${clinicEMR}`;
          }

          if (this.envisionType) {
            urlParams += `&envisionType=${this.envisionType}&companyId=${this.getDepId(this.envisionType)}`;
          }

          if (fromStreamLine) {
            urlParams += `&sl=${fromStreamLine}`;
          }

          if (fromStreamLine) {
            urlParams += `&slf=${facility}`;
          }

          const url = `${this.env.rxPhoto}${urlParams}`;

          if (currentLinkLength === 2) {
            if (clinicEMR && !this.router.url.includes('?code')) {
              return this.dialogService.prompt(
                RxDialogComponent,
                this._dialogConfig.incorrectEMRIntegration(),
              );
            } else if (!clinicEMR || window.location.search) {
              return (document.location.href = url);
            }
          }

          if (!currentEMRLink.includes(clinicEMR) && clinicEMR !== 'ennoview') {
            this.snackbarService.notify(
              `Sorry, but your clinic has no ${clinicTitle} integration`,
              this.snackbarType.error,
            );
            return this.router.navigate(['login']);
          }

          return (document.location.href = url);
        });
    }
  }

  //#region Canva Integration
  public handleCanvaIntegration(clinicId: any): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('user') as string;
    const state = urlParams.get('state') as string;

    if (!userId || !state) {
      return false;
    }

    this.updateClinicWithCanvaUserId(clinicId, userId).subscribe(
      () => this.redirectToCanva(true, state),
      () => this.redirectToCanva(false, state),
    );

    return true;
  }

  private updateClinicWithCanvaUserId(clinicId: any, userId: string): Observable<any> {
    const url = `${environment.baseAPIUrlv3}clinic/${clinicId}/`;
    const body = { canva_user_id: userId };
    return this.http.patch(url, body);
  }

  private redirectToCanva(success: boolean, state: string): void {
    const successParam = success ? 'true' : 'false';
    const redirectUrl = `https://canva.com/apps/configured?success=${successParam}&state=${state}`;
    document.location.href = redirectUrl;
  }

  //#endregion

  init() {
    this.activatedRoute.queryParams.subscribe((res: any) => {
      if (res.token) {
        localStorage.setItem(environment.localStorage.token, res.token);
        localStorage.setItem(environment.localStorage.userInfo, res.id);
        this.router.navigate([], { queryParams: { token: null } });
      }
    });
  }

  async returnUrl() {
    // if (localStorage.getItem(this.env.localStorage.returnUrl)) {
    //     this.router.navigate([localStorage.getItem(this.env.localStorage.returnUrl)])
    // } else {
    this.router.navigate([], { queryParams: { token: null } });
    // }
  }

  // TODO: temporary solution but it is faster
  getQueryParam(): object {
    const regex = /[?&]([^=#]+)=([^&#]*)/g;
    const url = window.location.search;
    const params = {};
    let match;
    while ((match = regex.exec(url))) {
      params[match[1]] = match[2];
    }

    return params;
  }

  ping() {
    return this.http
      .get(`${environment.baseAPIUrlv3}ping/`)
      .pipe(take(1))
      .subscribe((data) => {
        //this.logDebug(this.getToken());
      });
    // this.whoAmI()
    //     .subscribe((data) => {});
  }

  // whoAmI(suppress403: boolean = false) {
  //     const params = suppress403 ? { 'x-skip-interceptor': 'true' } : {};
  //     const headers = suppress403 ? { 'x-skip-interceptor': 'true' } : {};
  //     return this.http.get(`${environment.baseAPIUrlv3}doctor/me/`, { headers, params }).pipe(take(1));
  // }

  whoAmI(suppress403 = false, token?: string) {
    const url = `${environment.baseAPIUrlv3}doctor/me/`;
    if (token !== undefined) {
      // Validate a candidate token without changing the active session or its auth interceptor.
      return new HttpClient(this.httpBackend)
        .get(url, {
          headers: { Authorization: `Token ${token}`, Accept: 'application/json' },
        })
        .pipe(take(1));
    }

    return this.http.get(url).pipe(take(1));
  }

  hasEmrRestriction() {
    const flag = this.cookieService.get('emrRestriction');
    return /true/i.test(flag);
  }

  private redirectByDomain() {
    switch (window.location.origin) {
      case this.env.rxPortal:
        console.log(1);
        document.location.href = `${this.env.rxPortal}/auth`;
        return;
      case this.env.rxHealthVuAdmin:
      case this.env.rxHealthVuManage:
        console.log(2);
        document.location.href = `${this.env.rxHealthVu}/admin/login`;
        return;
      case this.env.rxHealthVu:
        console.log(3);
        const path = location.pathname.match('/admin') ? '/admin/login' : '/login';
        //const path = location.pathname.match('/admin') ? '/admin/login' : '/v19/login';
        document.location.href = `${this.env.rxHealthVu}${path}`;
        return;
      case this.env.rxAdmin:
        console.log(4);
        document.location.href = `${this.env.rxAdmin}/login`;
        return;
      default:
        console.log(5);
        document.location.href = this.env.rxAuth;
    }
  }

  getHash() {
    return localStorage.getItem('hash') || '';
  }

  setHash(hash) {
    localStorage.setItem('hash', hash);
  }

  setCanvaOAuthData(urlParams: any) {
    const paramsObject: Record<string, string> = {};

    urlParams.forEach((value, key) => {
      paramsObject[key] = value;
    });

    localStorage.setItem(this.env.localStorage.canvaOAuth, JSON.stringify(paramsObject));
  }
  getCanvaOAuthData(): any {
    return localStorage.getItem(this.env.localStorage.canvaOAuth);
  }
  hasCanvaOAuthData(urlParams: URLSearchParams) {
    return (
      urlParams.has('client_id') &&
      urlParams.has('code_challenge') &&
      urlParams.has('code_challenge_method') &&
      urlParams.has('redirect_uri') &&
      urlParams.has('response_type') &&
      urlParams.has('scope') &&
      urlParams.has('state')
    );
  }
  processCanvaOAuth() {
    const canvaOAuthDataString = this.getCanvaOAuthData();
    const canvaOAuthData = JSON.parse(canvaOAuthDataString);
    const token = this.getToken();

    const baseURL = `${this.env.localStorage.canvaOAuthUrl}?${new URLSearchParams(canvaOAuthData).toString()}`;

    this.http
      .get(baseURL, {
        headers: new HttpHeaders({
          Authorization: `Token ${token}`,
        }),
      })
      .pipe(take(1))
      .subscribe(
        (data: any) => {
          window.location.href = data.redirect_url;
        },
        (error: any) => {
          console.error('Error:', error);
          this.errorService.notify('Request Failed!');
        },
      );
  }
}
